<template>
  <div>
    <v-alert
      dense
      outlined
      type="info"
      border="left"
      class="mb-3"
    >
      Dicionários e apelidos da organização
      <strong>{{ org.nome }}</strong> (id <code>{{ org.id }}</code>).
    </v-alert>

    <v-tabs
      v-model="activeTab"
      background-color="transparent"
      color="secondary"
      slider-color="secondary"
    >
      <v-tab>Convênios</v-tab>
      <v-tab>Apelidos CBHPM</v-tab>
      <v-tab>Apelidos Especialidades</v-tab>
      <v-tab>Tipos de Agendamento</v-tab>
    </v-tabs>

    <v-tabs-items
      v-model="activeTab"
      class="pt-3"
    >
      <!-- ====================== Convênios ====================== -->
      <v-tab-item>
        <v-card flat>
          <v-card-actions class="px-0">
            <v-spacer />
            <v-btn
              color="secondary"
              small
              @click="openConvenioDialog()"
            >
              <v-icon
                left
                small
              >
                mdi-plus
              </v-icon>
              Novo convênio
            </v-btn>
          </v-card-actions>

          <v-progress-linear
            v-if="loading.convenios"
            indeterminate
            color="secondary"
          />
          <v-data-table
            :headers="headers.convenios"
            :items="convenios"
            :items-per-page="20"
            dense
            class="elevation-0"
          >
            <template #item.ativo="{ item }">
              <v-chip
                :color="item.ativo ? 'success' : 'grey'"
                x-small
                dark
              >
                {{ item.ativo ? 'Ativo' : 'Inativo' }}
              </v-chip>
            </template>
            <template #item.actions="{ item }">
              <v-btn
                icon
                x-small
                color="error"
                @click="confirmDelete('convenios', item)"
              >
                <v-icon small>
                  mdi-delete
                </v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <!-- ====================== Apelidos CBHPM ====================== -->
      <v-tab-item>
        <v-card flat>
          <v-row
            align="center"
            dense
          >
            <v-col
              cols="12"
              sm="6"
              md="4"
            >
              <v-text-field
                v-model="cbhpmQuery"
                label="Buscar (apelido / código / descrição)"
                dense
                hide-details
                clearable
                @keyup.enter="loadCbhpm()"
                @click:clear="loadCbhpm()"
              />
            </v-col>
            <v-col cols="auto">
              <v-btn
                small
                @click="loadCbhpm()"
              >
                <v-icon
                  left
                  small
                >
                  mdi-magnify
                </v-icon> Buscar
              </v-btn>
            </v-col>
            <v-spacer />
            <v-col cols="auto">
              <v-btn
                color="secondary"
                small
                @click="openCbhpmDialog()"
              >
                <v-icon
                  left
                  small
                >
                  mdi-plus
                </v-icon> Novo apelido
              </v-btn>
            </v-col>
          </v-row>

          <v-progress-linear
            v-if="loading.cbhpm"
            indeterminate
            color="secondary"
          />
          <v-data-table
            :headers="headers.cbhpm"
            :items="cbhpm"
            :server-items-length="cbhpmTotal"
            :options.sync="cbhpmOpts"
            :footer-props="{ itemsPerPageOptions: [10, 20, 50, 100] }"
            dense
            class="elevation-0"
          >
            <template #item.actions="{ item }">
              <v-btn
                icon
                x-small
                @click="openCbhpmDialog(item)"
              >
                <v-icon small>
                  mdi-pencil
                </v-icon>
              </v-btn>
              <v-btn
                icon
                x-small
                color="error"
                @click="confirmDelete('cbhpm', item)"
              >
                <v-icon small>
                  mdi-delete
                </v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <!-- ====================== Apelidos Especialidades ====================== -->
      <v-tab-item>
        <v-card flat>
          <v-row
            align="center"
            dense
          >
            <v-col
              cols="12"
              sm="6"
              md="4"
            >
              <v-text-field
                v-model="espQuery"
                label="Buscar (apelido / especialidade)"
                dense
                hide-details
                clearable
                @keyup.enter="loadEspecialidades()"
                @click:clear="loadEspecialidades()"
              />
            </v-col>
            <v-col cols="auto">
              <v-btn
                small
                @click="loadEspecialidades()"
              >
                <v-icon
                  left
                  small
                >
                  mdi-magnify
                </v-icon> Buscar
              </v-btn>
            </v-col>
            <v-spacer />
            <v-col cols="auto">
              <v-btn
                color="secondary"
                small
                @click="openEspDialog()"
              >
                <v-icon
                  left
                  small
                >
                  mdi-plus
                </v-icon> Novo apelido
              </v-btn>
            </v-col>
          </v-row>

          <v-progress-linear
            v-if="loading.especialidades"
            indeterminate
            color="secondary"
          />
          <v-data-table
            :headers="headers.especialidades"
            :items="especialidades"
            :server-items-length="espTotal"
            :options.sync="espOpts"
            :footer-props="{ itemsPerPageOptions: [10, 20, 50, 100] }"
            dense
            class="elevation-0"
          >
            <template #item.actions="{ item }">
              <v-btn
                icon
                x-small
                @click="openEspDialog(item)"
              >
                <v-icon small>
                  mdi-pencil
                </v-icon>
              </v-btn>
              <v-btn
                icon
                x-small
                color="error"
                @click="confirmDelete('especialidades', item)"
              >
                <v-icon small>
                  mdi-delete
                </v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>

      <!-- ====================== Tipos de Agendamento ====================== -->
      <v-tab-item>
        <v-card flat>
          <v-card-actions class="px-0">
            <v-spacer />
            <v-btn
              color="secondary"
              small
              @click="openTipoDialog()"
            >
              <v-icon
                left
                small
              >
                mdi-plus
              </v-icon> Novo tipo
            </v-btn>
          </v-card-actions>

          <v-progress-linear
            v-if="loading.tipos"
            indeterminate
            color="secondary"
          />
          <v-data-table
            :headers="headers.tipos"
            :items="tipos"
            :items-per-page="20"
            dense
            class="elevation-0"
          >
            <template #item.ativo="{ item }">
              <v-chip
                :color="item.ativo ? 'success' : 'grey'"
                x-small
                dark
              >
                {{ item.ativo ? 'Ativo' : 'Inativo' }}
              </v-chip>
            </template>
            <template #item.actions="{ item }">
              <v-btn
                icon
                x-small
                @click="openTipoDialog(item)"
              >
                <v-icon small>
                  mdi-pencil
                </v-icon>
              </v-btn>
              <v-btn
                icon
                x-small
                color="error"
                @click="confirmDelete('tipos', item)"
              >
                <v-icon small>
                  mdi-delete
                </v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <!-- ====================== Convênio dialog ====================== -->
    <v-dialog
      v-model="dlg.convenio.show"
      max-width="480"
    >
      <v-card>
        <v-card-title class="text-h6">
          Novo convênio
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="dlg.convenio.descricao"
            label="Descrição (convênio customizado)"
            hint="Use isto OU o ID do dicionário abaixo"
            persistent-hint
            dense
          />
          <v-text-field
            v-model.number="dlg.convenio.id_dicionario_convenios"
            label="ID dicionário convênios"
            type="number"
            dense
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="dlg.convenio.show = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="secondary"
            :loading="dlg.convenio.saving"
            @click="saveConvenio()"
          >
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ====================== CBHPM dialog ====================== -->
    <v-dialog
      v-model="dlg.cbhpm.show"
      max-width="480"
    >
      <v-card>
        <v-card-title class="text-h6">
          {{ dlg.cbhpm.id ? 'Editar' : 'Novo' }} apelido CBHPM
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model.number="dlg.cbhpm.id_dicionario_cbhpm"
            label="ID dicionário CBHPM"
            type="number"
            :disabled="!!dlg.cbhpm.id"
            dense
          />
          <v-text-field
            v-model="dlg.cbhpm.apelido"
            label="Apelido"
            dense
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="dlg.cbhpm.show = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="secondary"
            :loading="dlg.cbhpm.saving"
            @click="saveCbhpm()"
          >
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ====================== Especialidades dialog ====================== -->
    <v-dialog
      v-model="dlg.esp.show"
      max-width="480"
    >
      <v-card>
        <v-card-title class="text-h6">
          {{ dlg.esp.id ? 'Editar' : 'Novo' }} apelido especialidade
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model.number="dlg.esp.id_dicionario_especialidades"
            label="ID dicionário especialidades"
            type="number"
            :disabled="!!dlg.esp.id"
            dense
          />
          <v-text-field
            v-model="dlg.esp.apelido"
            label="Apelido"
            dense
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="dlg.esp.show = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="secondary"
            :loading="dlg.esp.saving"
            @click="saveEsp()"
          >
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ====================== Tipo agendamento dialog ====================== -->
    <v-dialog
      v-model="dlg.tipo.show"
      max-width="520"
    >
      <v-card>
        <v-card-title class="text-h6">
          {{ dlg.tipo.id ? 'Editar' : 'Novo' }} tipo de agendamento
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="dlg.tipo.nome"
            label="Nome"
            dense
          />
          <v-text-field
            v-model="dlg.tipo.descricao"
            label="Descrição"
            dense
          />
          <v-text-field
            v-model.number="dlg.tipo.duracao_minutos"
            label="Duração (minutos)"
            type="number"
            dense
          />
          <v-textarea
            v-model="dlg.tipo.texto_preparo"
            label="Texto de preparo"
            rows="2"
            auto-grow
            dense
          />
          <v-switch
            v-if="dlg.tipo.id"
            v-model="dlg.tipo.ativo"
            label="Ativo"
            inset
            dense
            hide-details
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="dlg.tipo.show = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="secondary"
            :loading="dlg.tipo.saving"
            @click="saveTipo()"
          >
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ====================== Delete confirm ====================== -->
    <v-dialog
      v-model="del.show"
      max-width="420"
    >
      <v-card>
        <v-card-title class="text-h6">
          Confirmar exclusão
        </v-card-title>
        <v-card-text>{{ del.message }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="del.show = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="error"
            :loading="del.saving"
            @click="doDelete()"
          >
            Excluir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3500"
      top
    >
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
  import { $k } from '@/lib/kraken'

  function emptyConvenioDlg () {
    return { show: false, saving: false, descricao: '', id_dicionario_convenios: null }
  }
  function emptyCbhpmDlg () {
    return { show: false, saving: false, id: null, id_dicionario_cbhpm: null, apelido: '' }
  }
  function emptyEspDlg () {
    return { show: false, saving: false, id: null, id_dicionario_especialidades: null, apelido: '' }
  }
  function emptyTipoDlg () {
    return { show: false, saving: false, id: null, nome: '', descricao: '', duracao_minutos: 30, texto_preparo: '', ativo: true }
  }

  export default {
    name: 'DictionaryTab',

    props: {
      org: { type: Object, required: true },
    },

    data: () => ({
      activeTab: 0,
      loading: { convenios: false, cbhpm: false, especialidades: false, tipos: false },
      loaded: { convenios: false, cbhpm: false, especialidades: false, tipos: false },

      convenios: [],
      cbhpm: [],
      cbhpmTotal: 0,
      cbhpmQuery: '',
      cbhpmOpts: { page: 1, itemsPerPage: 20 },
      especialidades: [],
      espTotal: 0,
      espQuery: '',
      espOpts: { page: 1, itemsPerPage: 20 },
      tipos: [],

      headers: {
        convenios: [
          { text: 'ID', value: 'id', width: 70 },
          { text: 'Descrição', value: 'descricao' },
          { text: 'Status', value: 'ativo', width: 90 },
          { text: 'Ações', value: 'actions', sortable: false, width: 80, align: 'end' },
        ],
        cbhpm: [
          { text: 'ID', value: 'id', width: 70 },
          { text: 'Código', value: 'cbhpm_codigo', width: 110 },
          { text: 'Procedimento', value: 'cbhpm_descricao' },
          { text: 'Apelido', value: 'apelido' },
          { text: 'Ações', value: 'actions', sortable: false, width: 100, align: 'end' },
        ],
        especialidades: [
          { text: 'ID', value: 'id', width: 70 },
          { text: 'Especialidade', value: 'especialidade_descricao' },
          { text: 'Apelido', value: 'apelido' },
          { text: 'Ações', value: 'actions', sortable: false, width: 100, align: 'end' },
        ],
        tipos: [
          { text: 'ID', value: 'id', width: 70 },
          { text: 'Nome', value: 'nome' },
          { text: 'Descrição', value: 'descricao' },
          { text: 'Duração', value: 'duracao_minutos', width: 100 },
          { text: 'Status', value: 'ativo', width: 90 },
          { text: 'Ações', value: 'actions', sortable: false, width: 100, align: 'end' },
        ],
      },

      dlg: {
        convenio: emptyConvenioDlg(),
        cbhpm: emptyCbhpmDlg(),
        esp: emptyEspDlg(),
        tipo: emptyTipoDlg(),
      },
      del: { show: false, saving: false, kind: '', item: null, message: '' },
      snackbar: { show: false, color: 'success', text: '' },
    }),

    watch: {
      'org.id': {
        immediate: true,
        handler () {
          // Reset and reload current tab when org changes
          this.loaded = { convenios: false, cbhpm: false, especialidades: false, tipos: false }
          this.loadActive()
        },
      },
      activeTab () { this.loadActive() },
      cbhpmOpts: {
        handler () { if (this.loaded.cbhpm) this.loadCbhpm() },
        deep: true,
      },
      espOpts: {
        handler () { if (this.loaded.especialidades) this.loadEspecialidades() },
        deep: true,
      },
    },

    methods: {
      flash (text, color = 'success') {
        this.snackbar = { show: true, color, text }
      },
      errMsg (err, fallback) {
        return err?.response?.data?.messages?.[0]?.text || err?.message || fallback
      },

      loadActive () {
        if (!this.org?.id) return
        if (this.activeTab === 0 && !this.loaded.convenios) this.loadConvenios()
        if (this.activeTab === 1 && !this.loaded.cbhpm) this.loadCbhpm()
        if (this.activeTab === 2 && !this.loaded.especialidades) this.loadEspecialidades()
        if (this.activeTab === 3 && !this.loaded.tipos) this.loadTipos()
      },

      // -------------------- Convênios --------------------
      async loadConvenios () {
        this.loading.convenios = true
        try {
          const res = await $k.manage.clinic_one.organizations._id.dictionaries.convenios.get({ id: this.org.id })
          this.convenios = res.convenios || []
          this.loaded.convenios = true
        } catch (err) {
          this.flash(this.errMsg(err, 'Falha ao carregar convênios.'), 'error')
        } finally {
          this.loading.convenios = false
        }
      },
      openConvenioDialog () {
        this.dlg.convenio = emptyConvenioDlg()
        this.dlg.convenio.show = true
      },
      async saveConvenio () {
        const d = this.dlg.convenio
        const body = { id: this.org.id }
        if (d.descricao && d.descricao.trim()) body.descricao = d.descricao.trim()
        if (d.id_dicionario_convenios) body.id_dicionario_convenios = d.id_dicionario_convenios
        if (!body.descricao && !body.id_dicionario_convenios) {
          this.flash('Informe descrição ou ID do dicionário.', 'error')
          return
        }
        d.saving = true
        try {
          await $k.manage.clinic_one.organizations._id.dictionaries.convenios.post(body)
          this.flash('Convênio criado.')
          d.show = false
          await this.loadConvenios()
        } catch (err) {
          this.flash(this.errMsg(err, 'Falha ao salvar convênio.'), 'error')
        } finally {
          d.saving = false
        }
      },

      // -------------------- CBHPM Apelidos --------------------
      async loadCbhpm () {
        this.loading.cbhpm = true
        try {
          const skip = (this.cbhpmOpts.page - 1) * this.cbhpmOpts.itemsPerPage
          const limit = this.cbhpmOpts.itemsPerPage
          const payload = { id: this.org.id, skip, limit }
          if (this.cbhpmQuery) payload.q = this.cbhpmQuery
          const res = await $k.manage.clinic_one.organizations._id.dictionaries.cbhpm_apelidos.get(payload)
          this.cbhpm = res.results || []
          this.cbhpmTotal = res.total || 0
          this.loaded.cbhpm = true
        } catch (err) {
          this.flash(this.errMsg(err, 'Falha ao carregar apelidos CBHPM.'), 'error')
        } finally {
          this.loading.cbhpm = false
        }
      },
      openCbhpmDialog (item) {
        this.dlg.cbhpm = emptyCbhpmDlg()
        if (item) {
          this.dlg.cbhpm.id = item.id
          this.dlg.cbhpm.id_dicionario_cbhpm = item.id_dicionario_cbhpm
          this.dlg.cbhpm.apelido = item.apelido
        }
        this.dlg.cbhpm.show = true
      },
      async saveCbhpm () {
        const d = this.dlg.cbhpm
        if (!d.apelido || !d.apelido.trim()) { this.flash('Apelido obrigatório.', 'error'); return }
        if (!d.id && !d.id_dicionario_cbhpm) { this.flash('ID dicionário CBHPM obrigatório.', 'error'); return }
        d.saving = true
        try {
          if (d.id) {
            await $k.manage.clinic_one.organizations._id.dictionaries.cbhpm_apelidos._idApelido.put({
              id: this.org.id, idApelido: d.id, apelido: d.apelido.trim(),
            })
          } else {
            await $k.manage.clinic_one.organizations._id.dictionaries.cbhpm_apelidos.post({
              id: this.org.id,
              id_dicionario_cbhpm: d.id_dicionario_cbhpm,
              apelido: d.apelido.trim(),
            })
          }
          this.flash('Apelido salvo.')
          d.show = false
          await this.loadCbhpm()
        } catch (err) {
          this.flash(this.errMsg(err, 'Falha ao salvar apelido CBHPM.'), 'error')
        } finally {
          d.saving = false
        }
      },

      // -------------------- Especialidades Apelidos --------------------
      async loadEspecialidades () {
        this.loading.especialidades = true
        try {
          const skip = (this.espOpts.page - 1) * this.espOpts.itemsPerPage
          const limit = this.espOpts.itemsPerPage
          const payload = { id: this.org.id, skip, limit }
          if (this.espQuery) payload.q = this.espQuery
          const res = await $k.manage.clinic_one.organizations._id.dictionaries.especialidades_apelidos.get(payload)
          this.especialidades = res.results || []
          this.espTotal = res.total || 0
          this.loaded.especialidades = true
        } catch (err) {
          this.flash(this.errMsg(err, 'Falha ao carregar apelidos de especialidades.'), 'error')
        } finally {
          this.loading.especialidades = false
        }
      },
      openEspDialog (item) {
        this.dlg.esp = emptyEspDlg()
        if (item) {
          this.dlg.esp.id = item.id
          this.dlg.esp.id_dicionario_especialidades = item.id_dicionario_especialidades
          this.dlg.esp.apelido = item.apelido
        }
        this.dlg.esp.show = true
      },
      async saveEsp () {
        const d = this.dlg.esp
        if (!d.apelido || !d.apelido.trim()) { this.flash('Apelido obrigatório.', 'error'); return }
        if (!d.id && !d.id_dicionario_especialidades) { this.flash('ID dicionário especialidades obrigatório.', 'error'); return }
        d.saving = true
        try {
          if (d.id) {
            await $k.manage.clinic_one.organizations._id.dictionaries.especialidades_apelidos._idApelido.put({
              id: this.org.id, idApelido: d.id, apelido: d.apelido.trim(),
            })
          } else {
            await $k.manage.clinic_one.organizations._id.dictionaries.especialidades_apelidos.post({
              id: this.org.id,
              id_dicionario_especialidades: d.id_dicionario_especialidades,
              apelido: d.apelido.trim(),
            })
          }
          this.flash('Apelido salvo.')
          d.show = false
          await this.loadEspecialidades()
        } catch (err) {
          this.flash(this.errMsg(err, 'Falha ao salvar apelido de especialidade.'), 'error')
        } finally {
          d.saving = false
        }
      },

      // -------------------- Tipos de Agendamento --------------------
      async loadTipos () {
        this.loading.tipos = true
        try {
          const res = await $k.manage.clinic_one.organizations._id.dictionaries.appointment_types.get({ id: this.org.id })
          this.tipos = res.tipos_agendamento || []
          this.loaded.tipos = true
        } catch (err) {
          this.flash(this.errMsg(err, 'Falha ao carregar tipos de agendamento.'), 'error')
        } finally {
          this.loading.tipos = false
        }
      },
      openTipoDialog (item) {
        this.dlg.tipo = emptyTipoDlg()
        if (item) {
          this.dlg.tipo.id = item.id
          this.dlg.tipo.nome = item.nome || ''
          this.dlg.tipo.descricao = item.descricao || ''
          this.dlg.tipo.duracao_minutos = item.duracao_minutos || 0
          this.dlg.tipo.texto_preparo = item.texto_preparo || ''
          this.dlg.tipo.ativo = !!item.ativo
        }
        this.dlg.tipo.show = true
      },
      async saveTipo () {
        const d = this.dlg.tipo
        if (!d.nome || !d.nome.trim()) { this.flash('Nome obrigatório.', 'error'); return }
        d.saving = true
        try {
          if (d.id) {
            await $k.manage.clinic_one.organizations._id.dictionaries.appointment_types._idTipoAgendamento.put({
              id: this.org.id,
              idTipoAgendamento: d.id,
              nome: d.nome.trim(),
              descricao: d.descricao || '',
              duracao_minutos: d.duracao_minutos || 0,
              texto_preparo: d.texto_preparo || '',
              ativo: d.ativo,
            })
          } else {
            await $k.manage.clinic_one.organizations._id.dictionaries.appointment_types.post({
              id: this.org.id,
              nome: d.nome.trim(),
              descricao: d.descricao || '',
              duracao_minutos: d.duracao_minutos || 0,
              texto_preparo: d.texto_preparo || '',
            })
          }
          this.flash('Tipo de agendamento salvo.')
          d.show = false
          await this.loadTipos()
        } catch (err) {
          this.flash(this.errMsg(err, 'Falha ao salvar tipo de agendamento.'), 'error')
        } finally {
          d.saving = false
        }
      },

      // -------------------- Delete --------------------
      confirmDelete (kind, item) {
        const labelMap = {
          convenios: `convênio "${item.descricao || item.id}"`,
          cbhpm: `apelido CBHPM "${item.apelido}"`,
          especialidades: `apelido de especialidade "${item.apelido}"`,
          tipos: `tipo de agendamento "${item.nome}"`,
        }
        this.del = {
          show: true,
          saving: false,
          kind,
          item,
          message: `Tem certeza que deseja excluir o ${labelMap[kind]}?`,
        }
      },
      async doDelete () {
        const { kind, item } = this.del
        this.del.saving = true
        try {
          if (kind === 'convenios') {
            await $k.manage.clinic_one.organizations._id.dictionaries.convenios._idConvenio.delete({
              id: this.org.id, idConvenio: item.id,
            })
            await this.loadConvenios()
          } else if (kind === 'cbhpm') {
            await $k.manage.clinic_one.organizations._id.dictionaries.cbhpm_apelidos._idApelido.delete({
              id: this.org.id, idApelido: item.id,
            })
            await this.loadCbhpm()
          } else if (kind === 'especialidades') {
            await $k.manage.clinic_one.organizations._id.dictionaries.especialidades_apelidos._idApelido.delete({
              id: this.org.id, idApelido: item.id,
            })
            await this.loadEspecialidades()
          } else if (kind === 'tipos') {
            await $k.manage.clinic_one.organizations._id.dictionaries.appointment_types._idTipoAgendamento.delete({
              id: this.org.id, idTipoAgendamento: item.id,
            })
            await this.loadTipos()
          }
          this.flash('Item excluído.')
          this.del.show = false
        } catch (err) {
          this.flash(this.errMsg(err, 'Falha ao excluir.'), 'error')
        } finally {
          this.del.saving = false
        }
      },
    },
  }
</script>
